// 支持递归浏览项目管理器的 DaVinci 项目选择器。
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  ChevronRight,
  Folder,
  FolderOpen,
} from 'lucide-react'
import { useRef, useState, type ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'

import { bridge } from '@maxus/website/lib/bridge'
import { cn } from '@maxus/website/utils'

import {
  Button,
  fieldTriggerClassName,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../base'

type DaVinciProjectPickerProps = Omit<
  ComponentProps<typeof Button>,
  'children' | 'disabled' | 'onClick' | 'type' | 'value' | 'variant'
> & {
  disabled: boolean
  projectName: string
  projectPath: string | null
  value: string | null
  onConfirm: (mapping: { davinciProjectName: string; davinciProjectPath: string }) => Promise<void>
}

type ProjectSelection = { name: string; path: string }

type ProjectManagerListProps = {
  depth: number
  disabled: boolean
  expandedPaths: Set<string>
  path: string
  selectedProject: ProjectSelection | null
  onExpandedChange: (path: string) => void
  onSelect: (project: ProjectSelection) => void
}

const getChildPath = (parentPath: string, name: string): string => (parentPath ? `${parentPath}/${name}` : `/${name}`)

const getProjectPath = (project: ProjectSelection): string => getChildPath(project.path, project.name)

const findExpandedProjectPaths = async (project: ProjectSelection): Promise<Set<string> | null> => {
  const expandedPaths = new Set<string>()
  let currentPath = ''

  try {
    for (const segment of project.path
      .split('/')
      .filter(Boolean)) {
      const entries = await bridge.listProjectManager({ project_manager_path: currentPath })
      if (!entries.some(entry => entry.type === 'folder' && entry.name === segment)) return null

      currentPath = getChildPath(currentPath, segment)
      expandedPaths.add(currentPath)
    }

    const entries = await bridge.listProjectManager({ project_manager_path: currentPath })
    if (!entries.some(entry => entry.type === 'project' && entry.name === project.name)) return null

    return expandedPaths
  } catch {
    return null
  }
}

const ProjectManagerList = ({
  depth,
  disabled,
  expandedPaths,
  path,
  selectedProject,
  onExpandedChange,
  onSelect,
}: ProjectManagerListProps) => {
  const { t } = useTranslation()
  const projectsQuery = useQuery({
    queryKey: ['resolve-bridge', 'project-manager', path],
    queryFn: () => bridge.listProjectManager({ project_manager_path: path }),
  })
  const entries = projectsQuery.data ?? []

  return (
    <div>
      {projectsQuery.isPending && (
        <p className="px-2 py-1.5 text-xs text-text-tertiary" style={{ paddingLeft: `${depth * 16 + 24}px` }}>
          {t('Loading')}
        </p>
      )}
      {projectsQuery.isError && (
        <p className="px-2 py-1.5 text-xs text-destructive" style={{ paddingLeft: `${depth * 16 + 24}px` }}>
          {t('Failed to load DaVinci projects')}
        </p>
      )}
      {!projectsQuery.isPending && !projectsQuery.isError && entries.length === 0 && (
        <p className="px-2 py-1.5 text-xs text-text-tertiary" style={{ paddingLeft: `${depth * 16 + 24}px` }}>
          {t('No DaVinci projects found')}
        </p>
      )}
      {entries.map(entry => {
        if (entry.type === 'project') {
          return (
            <Button
              aria-current={selectedProject?.name === entry.name && selectedProject.path === path ? 'true' : undefined}
              className="min-w-0 w-full justify-start px-1.5"
              disabled={disabled}
              key={`project:${entry.name}`}
              onClick={() => onSelect({ name: entry.name, path })}
              style={{ paddingLeft: `${depth * 16 + 32}px` }}
              type="button"
              variant="ghost"
            >
              <Box className="size-4 shrink-0 text-primary" />
              <span className="truncate">{entry.name}</span>
            </Button>
          )
        }

        const childPath = getChildPath(path, entry.name)
        const expanded = expandedPaths.has(childPath)
        return (
          <div key={`folder:${childPath}`}>
            <div className="flex min-w-0 items-center gap-1" style={{ paddingLeft: `${depth * 16}px` }}>
              <Button
                aria-label={expanded ? t('Collapse folder') : t('Expand folder')}
                className="size-6"
                disabled={disabled}
                onClick={() => onExpandedChange(childPath)}
                size="icon-xs"
                type="button"
                variant="ghost"
              >
                <ChevronRight className={cn('transition-transform', expanded && 'rotate-90')} />
              </Button>
              <span className="flex min-w-0 flex-1 items-center gap-2 px-1.5 text-sm">
                {expanded ? (
                  <FolderOpen className="size-4 shrink-0 text-primary" />
                ) : (
                  <Folder className="size-4 shrink-0 text-primary" />
                )}
                <span className="truncate">{entry.name}</span>
              </span>
            </div>
            {expanded && (
              <ProjectManagerList
                depth={depth + 1}
                disabled={disabled}
                expandedPaths={expandedPaths}
                path={childPath}
                selectedProject={selectedProject}
                onExpandedChange={onExpandedChange}
                onSelect={onSelect}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

export const DaVinciProjectPicker = ({
  className,
  disabled,
  projectName,
  projectPath,
  value,
  onConfirm,
  ...triggerProps
}: DaVinciProjectPickerProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState<ProjectSelection | null>(null)
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(() => new Set())
  const [isConfirming, setIsConfirming] = useState(false)
  const openRequestIdRef = useRef(0)
  const displayPath = value ? getChildPath(projectPath ?? '', value) : ''

  const handleOpenChange = (nextOpen: boolean) => {
    const requestId = ++openRequestIdRef.current
    setOpen(nextOpen)
    if (!nextOpen) return

    setSelectedProject(null)
    setExpandedPaths(new Set())
    if (!value) return

    const project = { name: value, path: projectPath ?? '' }
    void findExpandedProjectPaths(project).then(matchingPaths => {
      if (openRequestIdRef.current !== requestId || !matchingPaths) return
      setSelectedProject(project)
      setExpandedPaths(matchingPaths)
    })
  }

  const handleExpandedChange = (path: string) => {
    openRequestIdRef.current += 1
    setExpandedPaths(current => {
      const next = new Set(current)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const handleSelect = (project: ProjectSelection) => {
    openRequestIdRef.current += 1
    setSelectedProject(project)
  }

  const handleConfirm = async () => {
    if (!selectedProject) return
    setIsConfirming(true)
    try {
      await onConfirm({ davinciProjectName: selectedProject.name, davinciProjectPath: selectedProject.path })
      setOpen(false)
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger asChild>
        <Button
          {...triggerProps}
          aria-label={t('Browse DaVinci project for {{name}}', { name: projectName })}
          className={cn(fieldTriggerClassName, 'justify-start', className)}
          disabled={disabled}
          title={displayPath || undefined}
          type="button"
          variant="outline"
        >
          <Box className="size-4 shrink-0" />
          <span className="truncate">{displayPath || t('Select a DaVinci project')}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[360px] p-2">
        <div className="max-h-72 overflow-auto py-1">
          <ProjectManagerList
            depth={0}
            disabled={disabled || isConfirming}
            expandedPaths={expandedPaths}
            path=""
            selectedProject={selectedProject}
            onExpandedChange={handleExpandedChange}
            onSelect={handleSelect}
          />
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 border-t border-border-subtle pt-2">
          <p className="min-w-0 truncate text-xs text-text-tertiary">
            {selectedProject ? getProjectPath(selectedProject) : t('Select a DaVinci project')}
          </p>
          <Button
            disabled={disabled || isConfirming || !selectedProject}
            onClick={handleConfirm}
            size="sm"
            type="button"
          >
            {t('Save')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
